/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { ReactNode } from 'react'

import { FiClock, FiEdit2, FiEye, FiEyeOff, FiTrash2 } from 'react-icons/fi'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui'
import { DetailSheet } from '@/components/ui/detail-sheet/DetailSheet'
import { useAuth } from '@/hooks'

import { ZOrderPicker } from './editor/ZOrderPicker'
import { type GeofenceCategory, LOW_FUEL_CATEGORY_TYPE_ID, type ManagedGeofence } from './types'

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-3 py-2 border-b border-base-200 text-sm">
    <dt className="text-base-content/60">{label}</dt>
    <dd className="min-w-0">{children}</dd>
  </div>
)

interface GeofenceDetailSheetProps {
  geofence: ManagedGeofence | undefined
  categories: Map<number, GeofenceCategory>
  /** Editing needs a desktop-sized screen. */
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
}

/** The open geofence: what it is, where, how big, and the way to its edit history. */
export const GeofenceDetailSheet = ({ geofence, categories, canEdit, onEdit, onDelete }: GeofenceDetailSheetProps) => {
  const { hasPermission } = useAuth()
  if (!geofence) return null

  const geofenceCategories = geofence.categoryIds
    .map((id) => categories.get(id))
    .filter((c): c is GeofenceCategory => !!c)
  const historySearch = new URLSearchParams({
    companyId: String(geofence.companyId),
    entityType: 'geofence',
    entityId: String(geofence.id),
    time: '90d',
  })

  const header = (
    <div className="flex w-full items-center gap-2 min-w-0">
      <span className="flex-1 truncate font-semibold" title={geofence.name}>
        {geofence.name}
      </span>
      <button
        type="button"
        className="btn btn-xs btn-ghost text-primary-content"
        onClick={onEdit}
        disabled={!canEdit}
        title={canEdit ? 'Edit geofence' : 'Editing geofences needs a larger screen'}
      >
        <FiEdit2 /> Edit
      </button>
      <button
        type="button"
        className="btn btn-xs btn-ghost btn-square text-primary-content"
        onClick={onDelete}
        aria-label="Delete geofence"
        title="Delete geofence"
      >
        <FiTrash2 />
      </button>
    </div>
  )

  return (
    <DetailSheet isOpen variant="pane" header={header} bodyKey={geofence.id}>
      <dl>
        <Row label="Category">
          {geofenceCategories.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {geofenceCategories.map((category) => (
                <Badge
                  key={category.id}
                  size="sm"
                  variant={category.typeId === LOW_FUEL_CATEGORY_TYPE_ID ? 'warning' : 'outline'}
                >
                  {category.name}
                </Badge>
              ))}
            </div>
          ) : (
            <span className="text-base-content/50">Not categorised</span>
          )}
        </Row>
        <Row label="Display layer">
          <ZOrderPicker value={geofence.zorder} />
          <p className="text-xs text-base-content/60">Where geofences overlap, higher layers are drawn on top.</p>
        </Row>
        <Row label="Visibility">
          <span className="flex items-center gap-2">
            {geofence.visible ? <FiEye /> : <FiEyeOff />}
            {geofence.visible ? 'Shown on maps' : 'Hidden from maps'}
          </span>
        </Row>
      </dl>
      {hasPermission('auditLogs') && (
        <Link
          to={{ pathname: '/audit-logs', search: historySearch.toString() }}
          className="link link-primary mt-4 inline-flex items-center gap-2 text-sm"
        >
          <FiClock /> View change history
        </Link>
      )}
    </DetailSheet>
  )
}
